/**
 * Selectores PUROS de la analítica de academia (F4-C): transforman el payload
 * del servidor en estructuras listas para las tarjetas y los mini-charts
 * (`DonutChart`/`BarChart`). Sin dependencias de Solid ni i18n → testeables con
 * vitest. Las ETIQUETAS legibles (i18n) las pone el componente.
 */
import type { AcademyMetrics, DebtMetrics, EnrollmentMetrics, RevenueMetrics } from "./api";

/** Centavos USD → USD (número con decimales). */
export const minorToUsd = (minor: number): number => minor / 100;

/** Orden canónico de los buckets de morosidad (espejo de Rust). */
export const AGING_BUCKET_ORDER = ["current", "1_30", "31_60", "61_90", "90_plus"] as const;
export type AgingBucketKey = (typeof AGING_BUCKET_ORDER)[number];

export interface MonthUsd {
  month: string;
  usdMinor: number;
}

/** Ingresos totales (USD centavos) por mes, agregando método × moneda. */
export function revenueByMonthTotals(revenue: RevenueMetrics): MonthUsd[] {
  const acc = new Map<string, number>();
  for (const p of revenue.byMonth) {
    acc.set(p.month, (acc.get(p.month) ?? 0) + p.usdMinor);
  }
  return [...acc.entries()]
    .map(([month, usdMinor]) => ({ month, usdMinor }))
    .toSorted((a, b) => a.month.localeCompare(b.month));
}

export interface MethodUsd {
  method: string;
  usdMinor: number;
}

/** Ingresos totales (USD centavos) por método de pago, desc. */
export function revenueByMethod(revenue: RevenueMetrics): MethodUsd[] {
  const acc = new Map<string, number>();
  for (const p of revenue.byMonth) {
    acc.set(p.method, (acc.get(p.method) ?? 0) + p.usdMinor);
  }
  return [...acc.entries()]
    .map(([method, usdMinor]) => ({ method, usdMinor }))
    .toSorted((a, b) => b.usdMinor - a.usdMinor);
}

/** Importe (USD centavos) por moneda original (para el desglose dual USD/VES). */
export function revenueByCurrency(revenue: RevenueMetrics): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const p of revenue.byMonth) {
    acc[p.currency] = (acc[p.currency] ?? 0) + p.usdMinor;
  }
  return acc;
}

/** Buckets de morosidad en el orden canónico, rellenando los ausentes con 0. */
export function orderedAgingBuckets(debt: DebtMetrics): {
  bucket: AgingBucketKey;
  usdMinor: number;
  count: number;
}[] {
  const byKey = new Map(debt.buckets.map((b) => [b.bucket, b]));
  return AGING_BUCKET_ORDER.map((bucket) => {
    const found = byKey.get(bucket);
    return { bucket, usdMinor: found?.usdMinor ?? 0, count: found?.count ?? 0 };
  });
}

/** Deuda VENCIDA (todo lo que no es `current`), en USD centavos. */
export function overdueTotalMinor(debt: DebtMetrics): number {
  return debt.buckets.filter((b) => b.bucket !== "current").reduce((sum, b) => sum + b.usdMinor, 0);
}

/** Tasa de conversión global del funnel (inscritos / total leads). 0 si no hay. */
export function funnelConversion(enrollment: EnrollmentMetrics): number {
  const total = enrollment.byStage.reduce((sum, s) => sum + s.count, 0);
  const enrolled = enrollment.byStage.find((s) => s.stage === "inscrito")?.count ?? 0;
  return total > 0 ? enrolled / total : 0;
}

/** Conteo de leads en una etapa concreta. */
export function stageCount(enrollment: EnrollmentMetrics, stage: string): number {
  return enrollment.byStage.find((s) => s.stage === stage)?.count ?? 0;
}

/** Bytes → texto humano (KB/MB/GB), base 1024. */
const KIB = 1024;
export function formatBytes(bytes: number): string {
  if (bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(KIB)));
  const value = bytes / KIB ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Suma total de certificados emitidos en la serie mensual. */
export function certificatesTotal(metrics: AcademyMetrics): number {
  return metrics.academic.certificatesByMonth.reduce((sum, m) => sum + m.count, 0);
}
